<template>
  <div class="flex flex-col grow bg-gradient-gray-neural">
    <div class="flex flex-col py-4 px-5 lg:px-6 gap-1">
      <div class="flex items-center gap-3">
        <FileType :extension="extension" variant="solid" size="md" />
        <EditableTextArea
          :key="title"
          :defaultValue="title"
          :maxLength="200"
          variant="title"
          submitMode="both"
          :readonly="!canUpdate"
          :class="
            canUpdate ? 'hover:cursor-pointer' : 'hover:cursor-not-allowed'
          "
          class="font-medium text-style-2xl lg:text-style-3xl"
          @update="handleUpdateTitle"
        />
      </div>
      <span class="self-end">
        {{ formatBytes(file.file.size) }}
      </span>
    </div>
    <div class="flex flex-col px-3 lg:px-6 pb-6 lg:pb-4 gap-6 lg:gap-4">
      <Dates
        :file="file"
        :readonly="!canUpdate"
        :expirationDate="expirationDate"
        :notificationDate="notificationDate"
      />
      <AdditionalInfo
        :defaultValue="description"
        :readonly="!canUpdate"
        :class="canUpdate ? 'hover:cursor-pointer' : 'hover:cursor-not-allowed'"
        @update="handleUpdateDescription"
      />
      <LinkedTo :file="file" :readonly="!canUpdate" />
      <Tags
        :readonly="!canUpdate"
        :title="$t('components.file_details.tags.title')"
        :description="$t('components.file_details.tags.description')"
        :initialState="file.tags"
        :buildingIds="[file.tenant.id]"
        @update="handleUpdateTags"
      />
    </div>
  </div>
</template>

<script setup>
// Import Vue Utilities
import { ref, computed, inject } from "vue";

// Import Utilities
import axios from "axios";
import { toast } from "@/SharedComponents/Toaster";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";

// Import Components
import { EditableTextArea } from "@/SharedComponents/Inputs";
import { FileType } from "@/SharedComponents/File";
import { Tags } from "@/SharedComponents/Parts";
import { Dates, LinkedTo, AdditionalInfo } from "@/Pages/Files/Partials/Detail";

// Import Utils
import { formatBytes } from "@/Utils/size";

// Emitter
const emitter = inject("emitter");

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  canUpdate: {
    type: Boolean,
    required: true,
  },
});

// Refs
const title = ref(props.file.title.replace(/\.[^/.]+$/, ""));
const extension = ref(props.file.title.split(".").pop());
const description = ref(props.file.description);

// Computed Props
const expirationDate = computed(() => {
  return props.file.expiration_date
    ? new Date(props.file.expiration_date)
    : null;
});

const notificationDate = computed(() => {
  return props.file.reminder_date ? new Date(props.file.reminder_date) : null;
});

// Functions
function handleUpdateTags(tags) {
  axios
    .post(route("api.files.tags"), {
      note_ids: [props.file.id],
      tag_ids: tags.map((tag) => tag.id),
    })
    .then((response) => {
      // Show the success message
      toast.success(t("toasts.files.update.success"));

      // Update
      emitter.emit("update-files-data", response.data);

      // Reload the router
      router.reload();
    })
    .catch((error) => {
      if (error.response.status == 403) {
        toast.error(t("toasts.files.update.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.update.error")
        );
      }
    });
}

const form = computed(() => ({
  ...props.file,
  tenant_id: props.file.tenant?.id || null,
  space_ids: props.file.spaces.map((space) => space.id),
  title: title.value,
  description: description.value,
}));

const handleUpdate = () => {
  router.put(
    route("files.update", [props.file]),
    {
      ...form.value,
    },
    {
      preserveScroll: true,
      onSuccess: (response) => {
        toast.success(t("toasts.files.update.success"));
        emitter.emit("update-files-data", { data: [response.props.file] });
      },
      onError: (error) => {
        console.log(error);
        toast.error(t("toasts.files.update.error"));
      },
      onFinish: () => {
        router.reload();
      },
    }
  );
};

const handleUpdateTitle = (value) => {
  title.value = value;

  handleUpdate();
};

const handleUpdateDescription = (value) => {
  description.value = value;

  handleUpdate();
};
</script>
